import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api, { setAuthToken } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithRedirect,
    loginWithPopup: auth0LoginWithPopup,
    logout: auth0Logout,
    getAccessTokenSilently
  } = useAuth0();

  const [user, setUser] = useState(null);
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up auth token interceptor
  useEffect(() => {
    if (isAuthenticated) {
      setAuthToken(getAccessTokenSilently);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch user data from our backend
  const fetchUser = useCallback(async () => {
    if (!isAuthenticated) {
      setUser(null);
      setStudio(null);
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();

      // Check if a signup role was stashed (from "Sign up as Student/Teacher")
      const signupRole = sessionStorage.getItem('dancehall_signup_role');
      const roleParam = signupRole ? `?role=${signupRole}` : '';
      if (signupRole) sessionStorage.removeItem('dancehall_signup_role');

      const { data } = await api.get(`/auth/me${roleParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data.user);
      setStudio(data.studio);
    } catch (error) {
      console.error('Failed to fetch user:', error.message);
      setUser(null);
      setStudio(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studio?.name) {
      document.title = studio.name;
    }
    if (studio?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studio.branding.logo;
      document.head.appendChild(link);
    }
  }, [studio]);

  useEffect(() => {
    if (!auth0Loading) {
      fetchUser();
    }
  }, [auth0Loading, fetchUser]);

  const login = (options) => loginWithRedirect(options);

  const loginPopup = async (options) => {
    await auth0LoginWithPopup(options);
    // Ensure auth token interceptor is set immediately
    setAuthToken(getAccessTokenSilently);
    // Fetch user data from our backend
    await fetchUser();
  };

  const logout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    setUser(null);
    setStudio(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      studio,
      isAuthenticated,
      loading: auth0Loading || loading,
      login,
      loginPopup,
      logout,
      refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
